/**
 * The personal blocklist: any employee blocks ONE address for themselves (user rule,
 * scope never_sync). Applied to their own mailbox only, so colleagues still get the mail.
 * Domains stay admin-only. live → /email-blocklist; mock → in-memory list.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmailBlacklistRuleDto } from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';
import { qk } from '../../lib/query-keys';

/** One personal rule as the screens show it. */
export interface MyBlockedSender {
  id: string;
  address: string;
  created_at: string;
  /** Set on a block RESULT when a firm-wide admin rule already covers the address, so no personal
   *  rule was created (the button shows "already blocked firm-wide" instead). */
  firm_wide_blocked?: boolean;
}

const MOCK_BLOCKED: MyBlockedSender[] = [
  { id: 'mock-block-1', address: 'newsletter@werbung-beispiel.ch', created_at: '2026-09-15T09:00:00.000Z' },
];
let mockCounter = 2;

/** Test hygiene: restore the demo list. */
export function resetMyBlocklistMock(): void {
  MOCK_BLOCKED.splice(0, MOCK_BLOCKED.length, {
    id: 'mock-block-1',
    address: 'newsletter@werbung-beispiel.ch',
    created_at: '2026-09-15T09:00:00.000Z',
  });
}

/** The demo list as admin-page rows, so mock mode shows a block on both screens. */
export function myBlocklistMockRules(): EmailBlacklistRuleDto[] {
  return MOCK_BLOCKED.map((b) => ({
    id: b.id,
    level: 'user',
    owner_user_id: 'mock-user',
    pattern: b.address,
    visibility_scope: 'never_sync',
    created_at: b.created_at,
    read_only: false,
  }));
}

/** Mock-mode write paths, shared with the admin page's own dialog and delete button. */
export function mockBlockAddress(address: string): MyBlockedSender {
  const pattern = address.trim().toLowerCase();
  const existing = MOCK_BLOCKED.find((b) => b.address === pattern);
  if (existing) return existing;
  const created = { id: `mock-block-${mockCounter++}`, address: pattern, created_at: new Date().toISOString() };
  MOCK_BLOCKED.push(created);
  return created;
}

export function mockUnblock(id: string): void {
  const i = MOCK_BLOCKED.findIndex((b) => b.id === id);
  if (i >= 0) MOCK_BLOCKED.splice(i, 1);
}

const toMine = (r: EmailBlacklistRuleDto): MyBlockedSender => ({
  id: r.id,
  address: r.pattern,
  created_at: r.created_at,
  firm_wide_blocked: r.firm_wide_blocked,
});

/** The caller's own personal rules (the editable user-level rows of GET /email-blocklist). */
export function useMyBlocklist() {
  const live = isLiveData();
  return useQuery<MyBlockedSender[]>({
    queryKey: [...qk.emailBlocklist(), 'mine', live ? 'live' : 'mock'],
    queryFn: async () => {
      if (!live) return [...MOCK_BLOCKED];
      const res = await apiClient.emailBlocklist.list();
      return res.rules.filter((r) => r.level === 'user' && !r.read_only).map(toMine);
    },
  });
}

/** Block one address for the caller (idempotent server-side). Rejects with the api's reason. */
export function useBlockSender() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: async (address: string): Promise<MyBlockedSender> => {
      const pattern = address.trim().toLowerCase();
      if (live) {
        return toMine(
          await apiClient.emailBlocklist.create({ level: 'user', pattern, visibility_scope: 'never_sync' }),
        );
      }
      return mockBlockAddress(pattern);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.emailBlocklist() }),
  });
}

/** Remove one of the caller's own rules. */
export function useUnblockSender() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: async (id: string) => {
      if (live) {
        await apiClient.emailBlocklist.remove(id);
        return;
      }
      mockUnblock(id);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.emailBlocklist() }),
  });
}
